import React, { useContext, useEffect, useState } from "react";
import "../../assets/styles/settings.css";
import { ThemeContext } from "../../context/ThemeContext";
import Toggle from "../../components/common/toggle";
import toast from "react-hot-toast";
import Button from "../../components/common/button";
import Modal from "../../components/common/modal";
import { FiEdit2 } from "react-icons/fi";
import { resolveImageUrl } from "../../utils/resolveImageUrl";

const normalizeHexColor = (color) => {
  if (!color) return "";
  const trimmed = String(color).trim();
  if (!trimmed) return "";
  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
};

const BrandingControls = ({ settings, onUpdate, isUpdating }) => {
  const { theme, setTheme } = useContext(ThemeContext);
  const [activeTheme, setActiveTheme] = useState("light");
  const branding = settings?.branding;

  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (branding) {
      setActiveTheme(branding.themeMode || "light");
      setTheme(branding.themeMode || "light");
      setPrimaryColor(branding.primaryColor || "");
      setSecondaryColor(branding.secondaryColor || "");
      setLogoUrl(branding.logoUrl || "");
    }
  }, [branding, setTheme]);

  const handleThemeChange = (newTheme) => {
    setActiveTheme(newTheme);
    setTheme(newTheme);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setLogoFile(e.target.files[0]);
    }
  };

  const resolveUrl = (path) => resolveImageUrl(path);

  const buildUpdateBody = () => {
    const brandingPayload = {
      ...(branding || {}),
      themeMode: activeTheme,
      primaryColor: normalizeHexColor(primaryColor),
      secondaryColor: normalizeHexColor(secondaryColor),
      logoUrl,
    };

    if (logoFile) {
      const body = new FormData();
      body.append("branding[themeMode]", activeTheme);
      body.append("branding[primaryColor]", normalizeHexColor(primaryColor));
      body.append("branding[secondaryColor]", normalizeHexColor(secondaryColor));
      body.append("logo", logoFile);
      return body;
    }

    return { branding: brandingPayload };
  };

  const handleUpdate = () => {
    if (!settings?._id) {
      toast.error("No settings ID found to update");
      return;
    }

    onUpdate(
      { id: settings._id, body: buildUpdateBody() },
      {
        onSuccess: () => {
          toast.success("Branding updated successfully!");
          setIsModalOpen(false);
          setLogoFile(null);
        },
        onError: (err) => {
          toast.error(
            err?.response?.data?.message || "Failed to update branding"
          );
        },
      }
    );
  };

  const formatColor = (color) => {
    if (!color) return "#5c308d";
    return normalizeHexColor(color);
  };

  if (!settings) return null;

  return (
    <div className="settingsForm">
      <div className="card-header">
        <div className="header-text">
          <h2>Branding Details</h2>
          <p>Customize your application colors and theme</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)} className="edit-icon-btn">
          <FiEdit2 /> {branding ? "Edit Branding" : "Set Up Branding"}
        </Button>
      </div>
      <hr className="divider" />

      {branding ? (
        <div className="branding-summary">
          <div className="summary-item">
            <label>Logo</label>
            <div className="summary-logo">
              {logoUrl ? (
                <img
                  src={resolveUrl(logoUrl)}
                  alt="Current Logo"
                  style={{ maxHeight: "40px", objectFit: "contain" }}
                />
              ) : (
                <span style={{ color: "#888", fontSize: "13px" }}>No logo set</span>
              )}
            </div>
          </div>
          <div className="summary-item">
            <label>Theme</label>
            <span className="theme-badge">{activeTheme}</span>
          </div>
          <div className="summary-item">
            <label>Primary</label>
            <div
              className="color-swatch"
              style={{ backgroundColor: formatColor(primaryColor) }}
            />
          </div>
          <div className="summary-item">
            <label>Secondary</label>
            <div
              className="color-swatch"
              style={{ backgroundColor: formatColor(secondaryColor) }}
            />
          </div>
        </div>
      ) : (
        <p>No branding data available. Click edit to set up your branding.</p>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Edit Branding"
      >
        <div className="edit-form">
          <div className="input-group full-width">
            <label>Company Logo</label>
            <div className="logo-upload">
              {(logoFile || logoUrl) && (
                <img
                  src={
                    logoFile ? URL.createObjectURL(logoFile) : resolveUrl(logoUrl)
                  }
                  alt="Logo Preview"
                  style={{
                    maxWidth: 150,
                    maxHeight: 60,
                    marginBottom: 10,
                    display: "block",
                    objectFit: "contain",
                  }}
                />
              )}
              <input type="file" onChange={handleFileChange} accept="image/*" />
              <p className="file-hint">Or enter URL:</p>
              <input
                type="text"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="color-inputs">
            <div className="input-group">
              <label>Primary Color</label>
              <div className="color-picker-wrapper">
                <input
                  type="color"
                  value={formatColor(primaryColor)}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#5C308D"
                />
              </div>
            </div>

            <div className="input-group">
              <label>Secondary Color</label>
              <div className="color-picker-wrapper">
                <input
                  type="color"
                  value={formatColor(secondaryColor)}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                />
                <input
                  type="text"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  placeholder="#5C308D"
                />
              </div>
            </div>
          </div>

          <div className="theme-toggle-section">
            <label>Theme Mode</label>
            <div className="toggle-row">
              <span>{activeTheme === "dark" ? "Dark Mode" : "Light Mode"}</span>
              <Toggle
                checked={activeTheme === "dark"}
                onChange={(checked) =>
                  handleThemeChange(checked ? "dark" : "light")
                }
              />
            </div>
          </div>

          <div className="modal-actions">
            <Button onClick={() => setIsModalOpen(false)} className="cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={isUpdating}
              className="save-btn"
            >
              {isUpdating ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BrandingControls;
